package de.katoonerie.content

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import de.katoonerie.components.Glasspane
import de.katoonerie.components.GlasspaneCloseButton
import de.katoonerie.components.YouTubeVideo
import de.katoonerie.contentful.Content
import de.katoonerie.contentful.Content.Media
import de.katoonerie.contentful.Image
import de.katoonerie.contentful.RichText
import de.katoonerie.lib.getFirstEntryText
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.ATarget
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.target
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLImageElement

@Composable
fun RichText.Document.Content(mediaAttrs: (AttrsScope<HTMLElement>.() -> Unit)? = null) {
    nodes.forEach { node ->
        when (node) {
            is Media.Gallery -> node.render(mediaAttrs)
            is Media.YouTube -> node.render(mediaAttrs)
            is Media.Actions -> node.render(mediaAttrs)
            is Image -> node.render()
            is RichText.Paragraph -> node.render()
        }
    }
}

fun AttrsScope<HTMLDivElement>.ContentAttrs(content: Content) {
    if (content.borderColor != null) {
        classes("border-solid", "border-24")
    }
    style {
        content.color?.let { color(it) }
        content.backgroundColor?.let { backgroundColor(it) }
        content.borderColor?.let { property("border-color", it) }
    }
}

@Composable
fun Media.Gallery.render(
    attrs: (AttrsScope<HTMLDivElement>.() -> Unit)? = null,
    imageAttrs: (AttrsScope<HTMLElement>.() -> Unit)? = null
) {
    Div(attrs = {
        ContentAttrs(this@render)
        attrs?.invoke(this)
        classes("not-prose")
    }) {
        images.forEach { image ->
            image.render(attrs = { imageAttrs?.invoke(this) })
        }
    }
}

@Composable
fun Media.YouTube.render(
    attrs: (AttrsScope<HTMLDivElement>.() -> Unit)? = null,
    videoAttrs: (AttrsScope<HTMLElement>.() -> Unit)? = null
) {
    Div(attrs = {
        ContentAttrs(this@render)
        attrs?.invoke(this)
        classes("not-prose")
    }) {
        videos.forEach { video ->
            YouTubeVideo(video.id, video.title, videoAttrs)
        }
    }
}

@Composable
fun Media.Actions.render(
    attrs: (AttrsScope<HTMLDivElement>.() -> Unit)? = null,
    imageAttrs: (AttrsScope<HTMLElement>.() -> Unit)? = null
) {
    var showing: Int by mutableStateOf(-1)

    Glasspane(doClose = { showing = -1 }, hideIf = { -1 == showing })

    Div(attrs = {
        ContentAttrs(this@render)
        attrs?.invoke(this)
        classes("not-prose")
    }) {
        actions.forEachIndexed { index, action ->
            action.image.render(attrs = {
                classes("cursor-pointer")
                imageAttrs?.invoke(this)
                onClick { showing = index }
            })
            ActionContainer(id = "action-$index", hideIf = { index != showing })
        }
    }

    GlasspaneCloseButton(doClose = { showing = -1 }, hideIf = { -1 == showing })

    LaunchedEffect("content-actions") {
        actions.forEachIndexed { index, action ->
            action.createFrame(index)
        }
    }
}

@Composable
fun ActionContainer(id: String, hideIf: () -> Boolean) {
    Div(attrs = {
        id(id)
        classes(
            "fixed",
            "top-2",
            "left-2",
            "right-2",
            "bottom-2",
            "sm:top-10",
            "sm:left-10",
            "sm:right-10",
            "sm:bottom-10",
            "bg-white",
            "border-24",
            "rounded-sm",
            "border-indigo-900"
        )
        style {
            if (hideIf()) {
                display(DisplayStyle.None)
            }
        }
    })
}

private suspend fun Media.Actions.Action.createFrame(index: Int) {
    val html = getFirstEntryText(archive.url)
    val element = document.getElementById("action-$index")
    if (element is Element) {
        val iframe = document.createElement("iframe")
        iframe.setAttribute("srcdoc", html)
        iframe.setAttribute("sandbox", "")
        iframe.setAttribute("class", "w-full h-full")
        element.appendChild(iframe)
    } else {
        console.error("element with id action-$index not loaded")
    }
}

@Composable
fun Image.render(attrs: (AttrsScope<HTMLImageElement>.() -> Unit)? = null) {
    Img(src = url.toString(), alt = title, attrs = { attrs?.invoke(this) })
}

@Composable
private fun RichText.Paragraph.render() {
    P {
        items.forEachIndexed { index, item ->
            if (index > 0) {
                Br()
            }
            when (item) {
                is RichText.Paragraph.Hyperlink ->
                    A(href = item.href, attrs = {
                        target(ATarget.Blank)
                    }) {
                        Text(item.value)
                    }

                is RichText.Paragraph.Text ->
                    Text(item.value)
            }
        }
    }
}
